/**
 * Content gateway: the single data-access boundary for V2 views.
 *
 * Every read goes through the FROZEN @mb-group/graphql-client. On a domain
 * NOT_FOUND we return null (the view renders 404). On any transport/availability
 * failure we flip into DEMO mode and serve the offline demo dataset, so the app
 * always looks complete — never a blank page.
 */
import type { DomainErrorCode, Locale } from "@mb-group/shared-types";
import { queries } from "@mb-group/graphql-client";
import type { GqlClient } from "../core/deps.js";
import { buildHref } from "../core/routes.js";
import { buildDemo } from "./demo.js";
import { plainText, typesetContent } from "./typography.js";
import type {
	Article,
	ArticleConnection,
	CaseStudy,
	Company,
	Footer,
	FormDefinition,
	HealthStatus,
	Navigation,
	Page,
	Resource,
	SearchHit,
	SearchResult,
	Service,
	ServiceCategory,
	ServiceConnection,
	Site,
	Webinar,
} from "./types.js";

const CLIENT_ERROR_CODES: DomainErrorCode[] = [
	"VALIDATION_FAILED",
	"UNAUTHENTICATED",
	"FORBIDDEN",
	"RATE_LIMITED",
	"QUERY_TOO_DEEP",
	"QUERY_TOO_COSTLY",
	"CONSENT_REQUIRED",
	"CAPTCHA_FAILED",
	"DUPLICATE_LEAD",
	"GATED_RESOURCE",
];

/** The domain code of a GraphQL client error (GraphQLDomainError#code), if any. */
export function errorCode(err: unknown): DomainErrorCode | undefined {
	if (err && typeof err === "object" && "code" in err) {
		const code = err.code;
		if (typeof code === "string") return code as DomainErrorCode;
	}
	return undefined;
}

export interface ServicesFilter {
	categorySlug?: string;
	materialStream?: string;
	first?: number;
	after?: string;
}

export class ContentGateway {
	constructor(
		private readonly client: GqlClient,
		private readonly site: string,
		private readonly getLocale: () => Locale,
		private readonly onModeChange: (demo: boolean) => void,
	) {}

	private demo = false;

	isDemoMode(): boolean {
		return this.demo;
	}

	private setDemo(on: boolean): void {
		if (this.demo === on) return;
		this.demo = on;
		this.onModeChange(on);
	}

	private base(): { site: string; locale: Locale } {
		return { site: this.site, locale: this.getLocale() };
	}

	private data() {
		return buildDemo(this.getLocale());
	}

	/**
	 * Run a query. `notFoundValue` is returned on domain NOT_FOUND (gateway is up).
	 * Any other failure flips DEMO mode and returns `fallback()`.
	 */
	private async read<T>(run: () => Promise<T>, fallback: () => T, notFoundValue?: T): Promise<T> {
		// The one place CMS content enters the app: hard spaces are added here,
		// for LIVE and DEMO alike (see typography.ts).
		return typesetContent(await this.fetch(run, fallback, notFoundValue), this.getLocale());
	}

	private async fetch<T>(run: () => Promise<T>, fallback: () => T, notFoundValue?: T): Promise<T> {
		try {
			const result = await run();
			this.setDemo(false);
			return result;
		} catch (err) {
			const code = errorCode(err);
			if (code === "NOT_FOUND" && notFoundValue !== undefined) {
				this.setDemo(false);
				return notFoundValue;
			}
			// Genuine client errors on a *read* are unexpected; still degrade gracefully.
			if (code && CLIENT_ERROR_CODES.includes(code) && notFoundValue !== undefined) {
				this.setDemo(false);
				return notFoundValue;
			}
			this.setDemo(true);
			return fallback();
		}
	}

	/**
	 * A read with no DEMO fallback, for draft previews. Fixture text under a
	 * "Wersja robocza" strip would pass the demo set off as the editor's draft,
	 * and a refused session would read as a missing page; so everything except
	 * NOT_FOUND propagates (UNAUTHENTICATED and FORBIDDEN included) and the
	 * preview view says what went wrong.
	 */
	private async readLive<T>(run: () => Promise<T>, notFoundValue: T): Promise<T> {
		let result: T;
		try {
			result = await run();
		} catch (err) {
			if (errorCode(err) !== "NOT_FOUND") throw err;
			result = notFoundValue;
		}
		this.setDemo(false);
		return typesetContent(result, this.getLocale());
	}

	// --- health --------------------------------------------------------------
	async health(): Promise<HealthStatus> {
		return this.read(
			async () => {
				const r = await this.client.query<{ health: HealthStatus }>(queries.HEALTH);
				return r.health;
			},
			() => ({ ok: false, version: "demo", services: [{ name: "gateway", ok: false }] }),
		);
	}

	/** Probe once at startup so the DEMO banner reflects reality immediately. */
	async probe(): Promise<void> {
		const h = await this.health();
		this.setDemo(!h.ok);
	}

	// --- chrome --------------------------------------------------------------
	/**
	 * Site-wide settings. Read for the home hero, which may carry an editor-chosen
	 * image; everything else about the site comes from the build.
	 *
	 * Returns null rather than a fixture when the gateway is unavailable: there is
	 * no honest demo value for "which image did the editor pick", and the caller
	 * already knows what to do without one — draw the authored diagram.
	 */
	async siteSettings(): Promise<Site | null> {
		return this.read(
			async () => {
				const r = await this.client.query<{ site: Site | null }>(queries.SITE, {
					key: this.site,
				});
				return r.site ?? null;
			},
			() => null,
		);
	}

	async navigation(): Promise<Navigation> {
		return this.read(
			async () => {
				const r = await this.client.query<{ navigation: Navigation | null }>(
					queries.NAVIGATION,
					this.base(),
				);
				return r.navigation ?? this.data().navigation;
			},
			() => this.data().navigation,
		);
	}

	async footer(): Promise<Footer> {
		return this.read(
			async () => {
				const r = await this.client.query<{ footer: Footer | null }>(queries.FOOTER, this.base());
				return r.footer ?? this.data().footer;
			},
			() => this.data().footer,
		);
	}

	// --- services ------------------------------------------------------------
	async serviceCategories(): Promise<ServiceCategory[]> {
		return this.read(
			async () => {
				const r = await this.client.query<{ serviceCategories: ServiceCategory[] }>(
					queries.SERVICE_CATEGORIES,
					this.base(),
				);
				return r.serviceCategories;
			},
			() => this.data().categories,
		);
	}

	async services(filter: ServicesFilter = {}): Promise<ServiceConnection> {
		return this.read(
			async () => {
				const r = await this.client.query<{ services: ServiceConnection }>(queries.SERVICES, {
					...this.base(),
					...filter,
				});
				return r.services;
			},
			() => {
				let list = this.data().services;
				if (filter.categorySlug)
					list = list.filter((s) => s.category?.slug === filter.categorySlug);
				if (filter.materialStream)
					list = list.filter((s) => s.materialStreams.includes(filter.materialStream!));
				return toServiceConnection(list);
			},
		);
	}

	async service(slug: string): Promise<Service | null> {
		return this.read(
			async () => {
				const r = await this.client.query<{ service: Service | null }>(queries.SERVICE, {
					...this.base(),
					slug,
				});
				return r.service;
			},
			() => this.data().services.find((s) => s.slug === slug) ?? null,
			null,
		);
	}

	// --- companies -----------------------------------------------------------
	async companies(): Promise<Company[]> {
		return this.read(
			async () => {
				const r = await this.client.query<{ companies: Company[] }>(queries.COMPANIES, this.base());
				return r.companies;
			},
			() => this.data().companies,
		);
	}

	async company(slug: string): Promise<Company | null> {
		return this.read(
			async () => {
				const r = await this.client.query<{ company: Company | null }>(queries.COMPANY, {
					...this.base(),
					slug,
				});
				return r.company;
			},
			() => this.data().companies.find((c) => c.slug === slug) ?? null,
			null,
		);
	}

	// --- case studies --------------------------------------------------------
	async caseStudies(serviceSlug?: string): Promise<CaseStudy[]> {
		return this.read(
			async () => {
				const r = await this.client.query<{ caseStudies: CaseStudy[] }>(queries.CASE_STUDIES, {
					...this.base(),
					serviceSlug,
				});
				return r.caseStudies;
			},
			() => {
				const all = this.data().caseStudies;
				if (!serviceSlug) return all;
				// A case study whose `services` were not selected cannot match a
				// service filter — exclude it instead of throwing.
				return all.filter((c) => (c.services ?? []).some((s) => s.slug === serviceSlug));
			},
		);
	}

	/**
	 * One case study. `preview` reads its DRAFT, which the gateway serves only to
	 * a signed-in editor (session cookie, assertPreview). A draft has no offline
	 * stand-in, so a preview never falls back to DEMO — see {@link readLive}.
	 */
	async caseStudy(slug: string, preview = false): Promise<CaseStudy | null> {
		const run = async () => {
			const r = await this.client.query<{ caseStudy: CaseStudy | null }>(queries.CASE_STUDY, {
				...this.base(),
				slug,
				preview,
			});
			return r.caseStudy;
		};
		if (preview) return this.readLive(run, null);
		return this.read(run, () => this.data().caseStudies.find((c) => c.slug === slug) ?? null, null);
	}

	/**
	 * The slug of `study` in `locale`, or null when it has no published version
	 * there (or the lookup failed). The public contract has no alternate slug,
	 * but a document keeps its documentId across languages, so the other
	 * language's list says; the DEMO set pairs the languages by position, as
	 * the seed does. Never flips DEMO mode: it only feeds the language switch
	 * and hreflang of a page that is already shown.
	 */
	async caseStudySlugIn(study: CaseStudy, locale: Locale): Promise<string | null> {
		if (study.isDemo) {
			const index = this.data().caseStudies.findIndex((c) => c.slug === study.slug);
			return index < 0 ? null : (buildDemo(locale).caseStudies[index]?.slug ?? null);
		}
		try {
			const r = await this.client.query<{ caseStudies: CaseStudy[] }>(queries.CASE_STUDIES, {
				site: this.site,
				locale,
			});
			return r.caseStudies.find((c) => c.documentId === study.documentId)?.slug ?? null;
		} catch {
			return null;
		}
	}

	// --- articles ------------------------------------------------------------
	async articles(tag?: string): Promise<ArticleConnection> {
		return this.read(
			async () => {
				const r = await this.client.query<{ articles: ArticleConnection }>(queries.ARTICLES, {
					...this.base(),
					tag,
				});
				return r.articles;
			},
			() => {
				let list = this.data().articles;
				if (tag) list = list.filter((a) => a.tags.includes(tag));
				return {
					edges: list.map((node) => ({ cursor: node.id, node })),
					pageInfo: { hasNextPage: false, endCursor: null, total: list.length },
				};
			},
		);
	}

	async article(slug: string): Promise<Article | null> {
		return this.read(
			async () => {
				const r = await this.client.query<{ article: Article | null }>(queries.ARTICLE, {
					...this.base(),
					slug,
				});
				return r.article;
			},
			() => this.data().articles.find((a) => a.slug === slug) ?? null,
			null,
		);
	}

	// --- resources & webinars ------------------------------------------------
	async resources(kind?: string): Promise<Resource[]> {
		return this.read(
			async () => {
				const r = await this.client.query<{ resources: Resource[] }>(queries.RESOURCES, {
					...this.base(),
					kind,
				});
				return r.resources;
			},
			() => {
				const list = this.data().resources;
				return kind ? list.filter((res) => res.kind === kind) : list;
			},
		);
	}

	async webinars(upcoming?: boolean): Promise<Webinar[]> {
		return this.read(
			async () => {
				const r = await this.client.query<{ webinars: Webinar[] }>(queries.WEBINARS, {
					...this.base(),
					upcoming,
				});
				return r.webinars;
			},
			() => {
				const now = Date.now();
				const list = this.data().webinars;
				if (upcoming === undefined) return list;
				return list.filter((w) => upcoming === new Date(w.startsAt).getTime() >= now);
			},
		);
	}

	// --- forms & pages -------------------------------------------------------
	async formDefinition(key: string): Promise<FormDefinition | null> {
		return this.read(
			async () => {
				const r = await this.client.query<{ formDefinition: FormDefinition | null }>(
					queries.FORM_DEFINITION,
					{ ...this.base(), key },
				);
				return r.formDefinition;
			},
			() => this.data().forms[key] ?? this.data().forms["contact"] ?? null,
			null,
		);
	}

	async page(slug: string, preview = false): Promise<Page | null> {
		return this.read(
			async () => {
				const r = await this.client.query<{ page: Page | null }>(queries.PAGE, {
					...this.base(),
					slug,
					preview,
				});
				return r.page;
			},
			() => this.data().pages[slug] ?? null,
			null,
		);
	}

	// --- search --------------------------------------------------------------
	async search(input: string): Promise<SearchResult> {
		// Callers normalise too (state, analytics); this guards every other caller.
		const query = plainText(input);
		return this.read(
			async () => {
				const r = await this.client.query<{ search: SearchResult }>(queries.SEARCH, {
					...this.base(),
					query,
				});
				return r.search;
			},
			() => this.demoSearch(query),
		);
	}

	private demoSearch(query: string): SearchResult {
		const locale = this.getLocale();
		const q = query.trim().toLowerCase();
		const data = this.data();
		const hits: SearchHit[] = [];
		const push = (
			id: string,
			contentType: SearchHit["contentType"],
			title: string,
			snippet: string,
			url: string,
		) => hits.push({ id, contentType, locale, title, snippet, url, score: 1 });

		if (q.length > 0) {
			const match = (...parts: string[]) => parts.join(" ").toLowerCase().includes(q);
			for (const s of data.services)
				if (match(s.title, s.summary, s.usp))
					push(
						s.id,
						"service",
						s.title,
						s.summary,
						buildHref("serviceDetail", locale, { slug: s.slug }),
					);
			for (const c of data.companies)
				if (match(c.name, c.description))
					push(
						c.id,
						"company",
						c.name,
						c.description,
						buildHref("companyDetail", locale, { slug: c.slug }),
					);
			for (const cs of data.caseStudies)
				if (match(cs.title, cs.challenge, cs.outcome))
					push(
						cs.id,
						"case_study",
						cs.title,
						cs.outcome,
						buildHref("caseStudyDetail", locale, { slug: cs.slug }),
					);
			for (const a of data.articles)
				if (match(a.title, a.excerpt))
					push(a.id, "article", a.title, a.excerpt, buildHref("article", locale, { slug: a.slug }));
		}

		return {
			query,
			total: hits.length,
			hits,
			tookMs: 0,
			suggestions:
				hits.length === 0 && q.length > 0 ? data.services.slice(0, 3).map((s) => s.title) : [],
		};
	}
}

function toServiceConnection(list: Service[]): ServiceConnection {
	return {
		edges: list.map((node) => ({ cursor: node.id, node })),
		pageInfo: { hasNextPage: false, endCursor: null, total: list.length },
	};
}
